<template>
  <div
    class="relative flex flex-col gap-1 border border-gray-100 p-4"
    :style="{ width: `${width}px` }"
  >
    <div v-if="type === 'yearly'" class="flex gap-[2px]">
      <AppButton
        class="!p-2 text-gray-400"
        :class="{ 'text-brand-yellow-500': yearlyMode === 'bolt' }"
        @click="changeYearlyMode('bolt')"
      >
        <AppSvgSymbol name="@escavador/lightning-bolt" size="16" />
      </AppButton>
      <AppButton
        class="!p-2 text-gray-400"
        :class="{ 'text-brand-yellow-500': yearlyMode === 'months' }"
        @click="changeYearlyMode('months')"
      >
        <AppSvgSymbol name="@escavador/calendar" size="16" />
      </AppButton>
    </div>
    <div id="escv-calendar__header" class="flex w-full flex-col gap-1">
      <div
        v-if="yearlyMode === 'months' || type === 'monthly'"
        class="relative z-20 flex flex-nowrap justify-between"
      >
        <button
          class="text-gray-400 hover:text-gray-600"
          @click="previousPeriod"
        >
          <AppSvgSymbol name="@escavador/arrow-left" size="16" />
        </button>
        <button
          class="text-gray-400 hover:text-gray-500"
          :disabled="
            disableFutureDates && type === 'yearly' && year === currentYear
          "
          @click="nextPeriod"
        >
          <AppSvgSymbol name="@escavador/arrow-right" size="16" />
        </button>
      </div>
      <div class="grid grid-cols-7">
        <template v-if="type === 'monthly'">
          <div
            v-for="name in daysNames"
            :key="name"
            class="px-[2px] text-center text-xs"
          >
            {{ name }}
          </div>
        </template>
      </div>
    </div>
    <div v-if="type === 'yearly'" class="mt-auto mb-auto">
      <template v-if="yearlyMode === 'months'">
        <div
          class="absolute left-0 right-0 top-12 z-10 text-sm font-medium text-center"
        >
          {{ year }}
        </div>
        <div class="grid grid-cols-2 p-1 gap-1">
          <button
            v-for="(month, index) in monthNames"
            :key="month"
            class="bg-white text-sm border rounded-md font-medium border-gray-100 text-left px-4 !py-2"
            :class="{
              'bg-brand-orange-200 text-brand-yellow-800 hover:bg-brand-orange-200':
                isFinalMonth(index),
              'bg-brand-orange-50 text-brand-yellow-700 hover:bg-brand-orange-100':
                isMiddleMonth(index),
              'text-gray-600 hover:bg-gray-100':
                !isMiddleMonth(index) && !isFinalMonth(index),
            }"
            :disabled="
              disableFutureDates &&
              type === 'yearly' &&
              year === currentYear &&
              index > currentMonth
            "
            @click="selectMonth(index)"
          >
            {{ month }}
          </button>
        </div>
      </template>

      <div
        v-if="yearlyMode === 'bolt'"
        class="grid grid-cols-1 p-1 gap-1 mt-auto mb-auto"
      >
        <button
          v-for="item in bolt"
          :key="item.label"
          class="bg-white rounded-md text-gray-600 text-sm border font-medium border-gray-100 !py-2 text-left px-4"
          :class="{
            'bg-brand-orange-200 text-brand-yellow-800 hover:bg-brand-orange-200':
              isSelectedPeriod(item.value),
            'hover:bg-gray-100': !isSelectedPeriod(item.value),
          }"
          @click="selectPeriod(item.value)"
        >
          {{ item.label }}
        </button>
      </div>
    </div>
    <div v-if="type === 'monthly'">
      <div
        v-for="month in months_"
        :key="month.key"
        :class="{
          hidden: currentMonth_.key !== month.key,
        }"
        role="presentation"
      >
        <div class="absolute left-0 right-0 top-4 z-10 text-center">
          {{ month.name }} {{ month.year }}
        </div>
        <div
          class="grid grid-flow-row gap-[1px] border border-gray-100 bg-gray-100"
        >
          <div
            v-for="(week, i) in month.weeks"
            :key="i"
            class="grid grid-cols-7 gap-[1px]"
          >
            <button
              v-for="day in week"
              :key="day && day.date && day.date.string"
              class="bg-white text-center hover:bg-gray-100"
              :class="{
                'bg-brand-brand-yellow-200': isFinalDate(day),
                'bg-brand-brand-yellow-50': isMiddleDate(day),
                '-mt-[1px]': day.day === 0 && day.type === 'left',
                '-ml-[1px]': day.day === 0 && day.type === 'left',
                '-mr-[1px]': day.day === 0 && day.type === 'right',
                '-mb-[1px]': day.day === 0 && day.type === 'right',
                'pointer-events-none': day.day === 0,
              }"
              :style="{ height: (width - 30) / 7 + 'px' }"
              @click="selectDate(day)"
            >
              {{ day.label || "" }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="controls" class="mt-2 flex justify-between gap-1">
      <button
        class="px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100"
        @click="resetDates"
      >
        Limpar
      </button>
      <button
        class="bg-sky-600 px-3 py-2 rounded-md text-sm font-medium text-white hover:bg-sky-700"
        :disabled="!dateOne && !dateTwo"
        @click="dateConfirmation"
      >
        Filtrar
      </button>
    </div>
  </div>
</template>
<script>
import format from "date-fns/format";
import getMonth from "date-fns/getMonth";
import getDaysInMonth from "date-fns/getDaysInMonth";
import startOfMonth from "date-fns/startOfMonth";
import subMonths from "date-fns/subMonths";
import addMonths from "date-fns/addMonths";
import isAfter from "date-fns/isAfter";
import isBefore from "date-fns/isBefore";
import startOfYear from "date-fns/startOfYear";
import addYears from "date-fns/addYears";
import subYears from "date-fns/subYears";
import subDays from "date-fns/subDays";
import differenceInDays from "date-fns/differenceInDays";

import AppSvgSymbol from "../AppSvgSymbol/index.vue";
import AppButton from "../AppButton/index.vue";

export default {
  name: "AppCalendar",
  components: {
    AppSvgSymbol,
    AppButton,
  },
  props: {
    type: {
      type: String,
      default: "monthly",
      validator: (value) => {
        return ["monthly", "yearly"].includes(value);
      },
    },
    controls: {
      type: Boolean,
      default: true,
    },
    width: {
      type: Number,
      default: 300,
    },
    daysNames: {
      type: Array,
      default: () => ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"],
    },
    monthNames: {
      type: Array,
      default: () => [
        "Janeiro",
        "Fevereiro",
        "Março",
        "Abril",
        "Maio",
        "Junho",
        "Julho",
        "Agosto",
        "Setembro",
        "Outubro",
        "Novembro",
        "Dezembro",
      ],
    },
    initialDate: {
      type: [Date, null],
      default: null,
    },
    selectedDateOne: {
      type: [Date, null],
      default: null,
    },
    selectedDateTwo: {
      type: [Date, null],
      default: null,
    },
    mode: {
      type: String,
      default: "range",
      validator: (value) => {
        return ["range", "single"].includes(value);
      },
    },
    disableFutureDates: {
      default: false,
      type: Boolean,
    },
  },
  emits: ["select-date", "start-date", "end-date", "date-confirmation"],
  data() {
    return {
      activeMonth: null,
      yearlyMode: "bolt",
      dateOne: null,
      dateTwo: null,
      months: [],
      currentDate: new Date(),
      year: format(new Date(), "yyyy"),
      currentYear: format(startOfYear(new Date()), "yyyy"),
      currentMonth: getMonth(new Date()) + 1,
      bolt: [
        { label: "Últimos 30 dias", value: 30 },
        { label: "Últimos 90 dias", value: 90 },
        { label: "Últimos 180 dias", value: 180 },
        { label: "Último ano", value: 360 },
        { label: "Últimos 2 anos", value: 720 },
        { label: "Últimos 3 anos", value: 1080 },
      ],
    };
  },
  computed: {
    today() {
      return new Date();
    },
    currentMonth_() {
      const month = startOfMonth(this.currentDate);
      return this.mountMonth(month);
    },
    months_() {
      const month = startOfMonth(this.currentDate);
      const months = [
        subMonths(month, 2),
        subMonths(month, 1),
        month,
        addMonths(month, 1),
        addMonths(month, 2),
      ];
      return months.map((month) => this.mountMonth(month));
    },
  },
  beforeMount() {
    this.dateOne = this.selectedDateOne
      ? this.createDayFromDate(this.selectedDateOne)
      : null;

    this.dateTwo = this.selectedDateTwo
      ? this.createDayFromDate(this.selectedDateTwo)
      : null;

    this.currentDate = this.initialDate || this.selectDateOne || this.today;

    this.year = this.dateOne
      ? parseInt(this.dateOne.date.object.getFullYear())
      : this.year;
  },
  methods: {
    changeYearlyMode(mode) {
      this.yearlyMode = mode;
    },
    isSelectedPeriod(period) {
      if (!(this.dateTwo?.date?.object && this.dateOne?.date?.object)) {
        return false;
      }

      return (
        differenceInDays(
          this.dateTwo?.date?.object,
          this.dateOne?.date?.object
        ) === period
      );
    },
    isFinalMonth(month) {
      return (
        (this.dateOne &&
          month === this.dateOne.date?.object.getMonth() &&
          parseInt(this.year) === this.dateOne.date?.object.getFullYear()) ||
        (this.dateTwo &&
          month === this.dateTwo.date?.object.getMonth() &&
          parseInt(this.year) === this.dateTwo.date?.object.getFullYear())
      );
    },
    isFinalDate(day) {
      return !day.date
        ? false
        : (this.dateOne && day.date.string === this.dateOne.date.string) ||
            (this.dateTwo && day.date.string === this.dateTwo.date.string);
    },
    isMiddleDate(day) {
      if (!day.date || !this.dateOne || !this.dateTwo) {
        return false;
      }
      return (
        isAfter(day.date.object, this.dateOne.date.object) &&
        isBefore(day.date.object, this.dateTwo.date.object)
      );
    },
    isMiddleMonth(month) {
      if (!this.dateOne || !this.dateTwo) {
        return false;
      }

      const start = format(this.dateOne.date.object, "yyyyMM");
      const end = format(this.dateTwo.date.object, "yyyyMM");
      const m = `${month + 1}`.padStart(2, "0");
      const current = `${this.year}${m}`;

      return (
        parseInt(current) > parseInt(start) && parseInt(current) < parseInt(end)
      );
    },
    resetDates() {
      this.dateOne = null;
      this.dateTwo = null;
      this.$emit("select-date", null);
      this.$emit("start-date", null);
      this.$emit("end-date", null);
      this.dateConfirmation();
    },
    selectMonth(month) {
      if (!this.dateOne || this.dateTwo || this.mode === "single") {
        this.dateOne = this.createDayFromDate(new Date(this.year, month, 1));
        this.$emit("select-date", this.dateOne);
        this.$emit("start-date", this.dateOne);
        this.dateTwo = null;
      } else {
        const dateTwo = this.createDayFromDate(new Date(this.year, month, 1));
        if (isBefore(dateTwo.date.object, this.dateOne.date.object)) {
          this.dateTwo = this.dateOne;
          this.dateOne = dateTwo;
          this.$emit("start-date", this.dateTwo);
        } else {
          this.dateTwo = this.createDayFromDate(new Date(this.year, month, 1));
        }
        this.$emit("select-date", this.dateTwo);
        this.$emit("end-date", this.dateTwo);
      }
    },
    selectPeriod(interval) {
      this.dateOne = this.createDayFromDate(subDays(this.today, interval));
      this.$emit("select-date", this.dateOne);
      this.$emit("start-date", this.dateOne);
      this.dateTwo = this.createDayFromDate(this.today);
      this.$emit("select-date", this.dateTwo);
      this.$emit("end-date", this.dateTwo);
    },
    selectDate(day) {
      if (!day.date) {
        return;
      }

      if (this.mode === "single") {
        this.dateOne = day;
        this.dateTwo = null;
        this.$emit("select-date", this.dateOne);
        return;
      }
      if (this.dateOne && isBefore(day.date.object, this.dateOne.date.object)) {
        this.dateOne = null;
        this.dateTwo = null;
      }

      if (!this.dateOne || this.dateTwo) {
        this.dateOne = day;
        this.dateTwo = null;
        this.$emit("select-date", this.dateOne);
        this.$emit("start-date", this.dateOne);
      } else {
        this.dateTwo = day;
        this.$emit("select-date", this.dateTwo);
        this.$emit("end-date", this.dateTwo);
      }
    },
    dateConfirmation() {
      this.$emit("date-confirmation", {
        dateOne: this.dateOne,
        dateTwo: this.dateTwo,
      });
    },
    mountMonth(month) {
      return {
        name: this.monthNames[getMonth(month)],
        year: format(month, "yyyy"),
        key: format(month, "MM-dd-yyyy"),
        start: month,
        weeks: this.weeks_(month),
      };
    },
    nextPeriod() {
      if (this.type === "monthly") {
        const month = startOfMonth(this.currentDate);
        this.currentDate = addMonths(month, 1);
      }
      if (this.type === "yearly") {
        if (this.disableFutureDates && this.year === this.currentYear) {
          return false;
        }
        const year = startOfYear(new Date(this.year, 0, 1));
        this.year = format(addYears(year, 1), "yyyy");
      }
    },
    previousPeriod() {
      if (this.type === "monthly") {
        const month = startOfMonth(this.currentDate);
        this.currentDate = subMonths(month, 1);
      }
      if (this.type === "yearly") {
        const year = startOfYear(new Date(this.year, 0, 1));
        this.year = format(subYears(year, 1), "yyyy");
      }
    },
    createDayFromDate(raw) {
      if (!raw) {
        return null;
      }
      const isoDate = new Date(raw);
      const day = format(isoDate, "d");
      const label = ("00" + day).slice(-2);
      const date = format(isoDate, "MM-dd-yyyy");

      return {
        day,
        type: "day",
        label,
        date: {
          object: new Date(
            parseInt(format(isoDate, "yyyy")),
            parseInt(format(isoDate, "M")) - 1,
            day
          ),
          string: date,
        },
      };
    },
    weeks_(date) {
      const dayOne = startOfMonth(date);
      const firstWeekDay = parseInt(format(dayOne, "e"));
      const daysInMonth = getDaysInMonth(dayOne);

      let weeks = [];
      let week = [];

      for (let i = 1; i < firstWeekDay; i++) {
        week = [...week, { day: 0, type: "left" }];
      }

      for (let day = 1; day <= daysInMonth; day++) {
        const label = ("00" + day).slice(-2);
        week = [
          ...week,
          {
            day,
            type: "day",
            label,
            date: {
              object: new Date(
                parseInt(format(dayOne, "yyyy")),
                parseInt(format(dayOne, "M")) - 1,
                day
              ),
              string:
                `${format(dayOne, "MM")}-` +
                label +
                `-${format(dayOne, "yyyy")}`,
            },
          },
        ];

        if (week.length === 7 || day === daysInMonth) {
          const faults = 7 - week.length;
          for (let i = 0; i < faults; i++) {
            week = [...week, { day: 0, type: "right" }];
          }
          weeks = [...weeks, week];
          week = [];
        }
      }

      return weeks;
    },
  },
};
</script>
